function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import classNames from 'classnames';
import { useCallback, useReducer } from 'react';
import { Typography, WithInfoIcon, useIsMounted } from 'seagull';
import { BillingDayOfMonthModel, BillingDayOfMonthType, serviceCostModel, serviceModel, serviceTemplateModel } from "../../../models";
import { utils } from "../../../utils";
import { StartBillingOnEnum } from "../../../types/api.model";
import { compact } from 'lodash-es';
import styles from "./ServicePicker.module.scss";
import ServicePickerPriceLabel from "./ServicePickerPriceLabel";
import { defaultBillingDayOfWeek, recurrencePeriodLabels } from "../../../constants";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export let Mode = /*#__PURE__*/function (Mode) {
  Mode["Services"] = "services";
  Mode["Bundles"] = "bundles";
  return Mode;
}({});
export const getColumnDefs = mode => {
  const isBundles = mode === Mode.Bundles;
  return compact([{
    value: isBundles ? 'Bundle Name' : 'Service Name',
    width: [160, 256],
    getItem: _ref => {
      let {
        service
      } = _ref;
      return {
        value: /*#__PURE__*/_jsx(Typography, {
          ellipsis: true,
          weight: "normal",
          className: classNames(styles.formElement, styles.absolutePosition, styles.withBackground),
          "data-testid": "uneditable-service-picker-row-name",
          children: service.name
        })
      };
    }
  }, !isBundles && {
    value: 'Rate',
    width: 180,
    getItem: _ref2 => {
      let {
        service
      } = _ref2;
      return {
        value: /*#__PURE__*/_jsx(ServicePickerPriceLabel, {
          service: service
        })
      };
    }
  }, isBundles && {
    value: 'Included Services',
    width: [160, 420],
    getItem: _ref3 => {
      let {
        service
      } = _ref3;
      return {
        value: service.bundle ? /*#__PURE__*/_jsx("div", {
          children: service.bundle.services.map(s => /*#__PURE__*/_jsxs("li", {
            className: styles.listItem,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.bullet
            }), /*#__PURE__*/_jsx(Typography, {
              children: s.name
            })]
          }, s.id))
        }) : null
      };
    }
  }, {
    value: 'Billing occurrence',
    width: 104,
    getItem: _ref4 => {
      let {
        service
      } = _ref4;
      return {
        value: /*#__PURE__*/_jsx(Typography, {
          children: serviceTemplateModel.isRecurring(service.billing) ? recurrencePeriodLabels[service.billing.recurrencePeriod] : 'One-time'
        })
      };
    }
  }, {
    value: /*#__PURE__*/_jsx(WithInfoIcon, {
      info: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("strong", {
          children: "Manual"
        }), ": Invoice is issued upon trigger of input units/hours or added charge to the billing cycle.", /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx("strong", {
          children: "Automatic"
        }), ": Invoice is issued on the date selected in the agreement."]
      }),
      children: "Billing trigger"
    }),
    width: 104,
    getItem: _ref5 => {
      let {
        service,
        index
      } = _ref5;
      return {
        value: /*#__PURE__*/_jsx(Typography, {
          "data-testid": "uneditable-service-picker-row-trigger-".concat(index),
          children: serviceTemplateModel.isManual(service.billing) ? 'Manual' : serviceTemplateModel.isOneTimeInStages(service.billing) ? 'In Stages' : 'Automatic'
        })
      };
    }
  }, !isBundles && {
    value: /*#__PURE__*/_jsx(WithInfoIcon, {
      info: /*#__PURE__*/_jsx(_Fragment, {
        children: "An upper limit for automatic billing. If exceeded or not specified, the bill will require manual client approval."
      }),
      children: "Preapproved"
    }),
    width: 120,
    getItem: _ref6 => {
      let {
        service
      } = _ref6;
      const unitCap = serviceTemplateModel.unitCap(service);
      const canHaveCap = serviceTemplateModel.isVariablePrice(service.cost);
      return {
        value: canHaveCap && /*#__PURE__*/_jsxs(Typography, {
          className: styles.preapproved,
          children: [/*#__PURE__*/_jsx(Typography, {
            className: styles.preapprovedInput,
            "data-testid": "uneditable-service-picker-row-cap",
            children: unitCap ? unitCap.toNumber() : ''
          }), !!unitCap && unitCap.greaterThan(0) && /*#__PURE__*/_jsx(Typography, {
            className: styles.capUnitName,
            ellipsis: true,
            skin: "secondary",
            children: serviceCostModel.formattedUnitNameShort(service.cost)
          })]
        })
      };
    }
  }]);
};

/** Newly created services will be highlighted. */
export const useNewServices = () => {
  const isMounted = useIsMounted();
  const [newServices, dispatch] = useReducer((state, action) => _objectSpread(_objectSpread({}, state), {}, {
    [action.serviceId]: action.isAdding
  }), {});
  const markServiceAsNew = useCallback(serviceId => {
    dispatch({
      isAdding: true,
      serviceId
    });
    setTimeout(() => {
      // Cleanup service from newServices so next time it's edited it will flash again.
      isMounted() && dispatch({
        isAdding: false,
        serviceId
      });
    }, 5000);
  }, []);
  return {
    newServices,
    markServiceAsNew
  };
};
export function getDiscountedPriceByServiceTemplate(serviceTemplate) {
  const {
    cost
  } = serviceTemplate;
  const {
    discount
  } = cost;
  return serviceTemplateModel.isFixedPrice(cost) || serviceTemplateModel.isVariablePrice(cost) ? utils.applyDiscount(cost.price, discount) : {
    minPrice: cost.minPrice != null ? utils.applyDiscount(cost.minPrice, discount) : null,
    maxPrice: utils.applyDiscount(cost.maxPrice, discount)
  };
}
export function getSkipBillingOnAcceptance(agreementData, businessSettings) {
  const isSkipBillingOnAcceptance = businessSettings !== null && businessSettings !== void 0 && businessSettings.billUpfront ? (businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.startBillingOn) === StartBillingOnEnum.SkipBillingOnAcceptance : true;
  return agreementData.isActiveAgreement() || isSkipBillingOnAcceptance;
}
export function serviceFromServiceTemplate(_ref7) {
  let {
    serviceTemplate,
    agreementData,
    businessSettings
  } = _ref7;
  return serviceModel.fromServiceTemplate(serviceTemplate, agreementData.serviceSource(), {
    isBilledUpfront: serviceTemplateModel.isRepeatableManual(serviceTemplate.billing) ? false : (businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billUpfront) || false,
    skipBillingOnAcceptance: getSkipBillingOnAcceptance(agreementData, businessSettings),
    billingDayOfWeek: serviceTemplateModel.isRepeatableManual(serviceTemplate.billing) ? 'Sunday' : (businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billingDayOfWeek) || defaultBillingDayOfWeek,
    billingDayOfMonth: BillingDayOfMonthModel.make(serviceTemplateModel.isRepeatableManual(serviceTemplate.billing) ? {
      type: BillingDayOfMonthType.LastDayOfMonth
    } : businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billingDayOfMonth),
    prorateFirstInvoice: businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.prorateFirstInvoice,
    isPaused: businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.automaticServicesPausedByDefault
  });
}